import { Component, Injector, OnInit, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { APP_CONFIG } from '../../core/config/app-config';
import { AuthService } from '../../core/auth/auth.service';
import { CurrentUserService } from '../../core/auth/current-user.service';

/**
 * Global-header profile menu. The account-circle icon opens a Material menu with the signed-in
 * user's general details (name, email, role, status) and a Log out action. Identity is sourced from
 * the effective `/me` role data ({@link CurrentUserService}) plus the MSAL active account for the
 * email/display name; both are resolved lazily so the component stays usable with auth disabled.
 */
@Component({
  selector: 'app-user-profile-menu',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatMenuModule, MatDividerModule],
  template: `
    <button
      mat-icon-button
      type="button"
      class="ript-profile-trigger"
      [matMenuTriggerFor]="menu"
      aria-label="Open profile menu"
    >
      <mat-icon>account_circle</mat-icon>
    </button>

    <mat-menu #menu="matMenu" xPosition="before" class="ript-profile-menu">
      <div class="ript-profile" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
        <div class="ript-profile__id">
          <mat-icon class="ript-profile__avatar" aria-hidden="true">account_circle</mat-icon>
          <div class="ript-profile__who">
            <span class="ript-profile__name">{{ displayName() }}</span>
            @if (email()) {
              <span class="ript-profile__email">{{ email() }}</span>
            }
          </div>
        </div>
        <dl class="ript-profile__details">
          <div>
            <dt>Role</dt>
            <dd>{{ rolesLabel() }}</dd>
          </div>
          <div>
            <dt>Facility</dt>
            <dd>{{ current.facility() || '—' }}</dd>
          </div>
          <div>
            <dt>Manager</dt>
            <dd>{{ current.manager() || '—' }}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{{ current.isActive() ? 'Active' : 'Inactive' }}</dd>
          </div>
        </dl>
      </div>

      <mat-divider />

      <button mat-menu-item type="button" (click)="onSignOut()">
        <mat-icon>logout</mat-icon>
        <span>Log out</span>
      </button>
    </mat-menu>
  `,
  styles: [
    `
      .ript-profile-trigger {
        color: #fff;
      }
      .ript-profile {
        padding: 0.85rem 1rem 0.5rem;
        min-width: 240px;
        cursor: default;
      }
      .ript-profile__id {
        display: flex;
        align-items: center;
        gap: 0.6rem;
      }
      .ript-profile__avatar {
        font-size: 2.25rem;
        width: 2.25rem;
        height: 2.25rem;
        color: #16325c;
        flex-shrink: 0;
      }
      .ript-profile__who {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      .ript-profile__name {
        font-weight: 700;
        font-size: 0.95rem;
        color: rgba(0, 0, 0, 0.87);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .ript-profile__email {
        font-size: 0.8rem;
        color: rgba(0, 0, 0, 0.6);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .ript-profile__details {
        margin: 0.75rem 0 0;
        display: grid;
        gap: 0.35rem;
        div {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          font-size: 0.8rem;
        }
        dt {
          color: rgba(0, 0, 0, 0.6);
        }
        dd {
          margin: 0;
          font-weight: 600;
          color: rgba(0, 0, 0, 0.87);
          text-align: right;
        }
      }
    `,
  ],
})
export class UserProfileMenuComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly config = inject(APP_CONFIG);
  readonly current = inject(CurrentUserService);

  private readonly account = signal<{ name?: string; username?: string } | null>(null);

  readonly email = computed(() => this.account()?.username ?? null);

  readonly displayName = computed(
    () =>
      this.current.name() ??
      this.account()?.name ??
      this.email() ??
      (this.config.authEnabled ? 'User' : 'Local Developer'),
  );

  readonly rolesLabel = computed(() => {
    const roles = this.current.roles();
    if (roles.length > 0) {
      return roles.join(', ');
    }
    return this.current.isAdmin() ? 'Administrator' : '—';
  });

  ngOnInit(): void {
    try {
      const account = this.injector.get(AuthService).getActiveAccount();
      if (account) {
        this.account.set({ name: account.name, username: account.username });
      }
    } catch {
      // MSAL unavailable (auth disabled or under test) — details fall back to /me.
    }
  }

  onSignOut(): void {
    void this.injector.get(AuthService).logout();
  }
}
